import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../services/api";
import SmartImg from "../components/SmartImg";

const imgSrc = (url) => (url?.startsWith("http") ? url : `${API_BASE}${url || ""}`);

// WhatsApp Business short link for management
const WHATSAPP_SHORT_LINK = "https://wa.me/message/5UT3QJZMUBKNP1";

function Modal({ open, onClose, children, title }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-lg sm:max-w-xl rounded-xl sm:rounded-2xl border border-white/10 bg-[var(--surface)] shadow-card max-h-[90vh] overflow-y-auto !text-slate-200"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/10">
          <h3 className="font-semibold truncate text-white">{title}</h3>
          <button
            className="btn-ghost px-2 py-1 rounded-lg text-slate-200"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-3 sm:p-4 !text-slate-200">{children}</div>
      </div>
    </div>
  );
}

export default function Team() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/api/public/team");
        setTeam(data || []);
      } catch {
        setErr("Failed to fetch team.");
        setTeam([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="page space-y-6">
      {/* Header with HLT background */}
      <section className="card relative overflow-hidden min-h-[140px] sm:min-h-[160px] md:min-h-[180px]">
        <div
          aria-hidden
          className="
            pointer-events-none absolute inset-y-0 right-0
            w-[44%] sm:w-[40%] md:w-[36%] lg:w-[34%]
            opacity-10
          "
          style={{ mixBlendMode: "lighten" }}
        >
          <img
            src="/hlt-management-image.png"
            alt=""
            className="
              h-full w-full
              object-cover
              object-right
              [object-position:right_55%] md:[object-position:right_50%]
            "
          />
        </div>

        <div className="relative">
          <h1 className="font-display text-3xl md:text-4xl text-white">Team</h1>
          <p className="mt-1 text-slate-300">
            <br />
            The people behind HeyLetsTravel.
            <br />
            <br />
            For any third-party bookings, collaborations, or brand partnerships with our DJs or creators, <br />
            please contact:
            <br />
            <span className="font-medium text-white">hlt-mgmt@hey-lets-travel.com</span>
          </p>

          {/* WhatsApp contact under email */}
          <div className="mt-2">
            <a
              href={WHATSAPP_SHORT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 underline underline-offset-4"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12.01 0C5.39 0 .03 5.36.03 11.98c0 2.11.55 4.17 1.6 5.99L0 24l6.22-1.62a11.97 11.97 0 0 0 5.78 1.49h.01c6.62 0 11.99-5.36 11.99-11.98a11.9 11.9 0 0 0-3.48-8.41zM12.01 21.8h-.01a9.82 9.82 0 0 1-4.99-1.36l-.36-.22-3.69.96.99-3.6-.24-.37a9.83 9.83 0 0 1-1.51-5.21c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.11 1.02 6.97 2.88a9.78 9.78 0 0 1 2.88 6.97c0 5.42-4.42 9.8-9.89 9.8zm5.6-7.35c-.31-.16-1.82-.9-2.1-1a.63.63 0 0 0-.61.08c-.17.12-.71.65-.87.87a.56.56 0 0 1-.6.2 9.82 9.82 0 0 1-3.06-1.89 11.53 11.53 0 0 1-2.13-2.64.63.63 0 0 1 .18-.85c.09-.08.2-.21.3-.31.1-.1.18-.2.27-.32a.6.6 0 0 0 .05-.64c-.06-.16-.48-1.17-.66-1.6-.18-.44-.37-.38-.54-.39h-.46a.9.9 0 0 0-.65.31 2.73 2.73 0 0 0-.84 2.03c0 1.2.87 2.36 1 2.53.12.16 1.73 2.66 4.2 3.74.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.46-.59 1.67-1.16.21-.57.21-1.06.14-1.16a.7.7 0 0 0-.33-.32z" />
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Error */}
      {err && (
        <div className="card bg-red-500/10 border-red-500/20 text-red-200">{err}</div>
      )}

      {/* Loading */}
      {loading && (
        <section className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-3 md:grid-cols-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="w-full aspect-[4/5] rounded-xl bg-white/5" />
              <div className="mt-3 h-5 w-1/2 bg-white/5 rounded" />
              <div className="mt-2 h-4 w-1/3 bg-white/5 rounded" />
            </div>
          ))}
        </section>
      )}

      {/* Grid */}
      {!loading && !err && (
        team.length === 0 ? (
          <section className="card text-slate-200">No team members yet.</section>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-3 md:grid-cols-4">
            {team.map((m) => (
              <li key={m.id}>
                <button
                  className="group relative w-full text-left"
                  onClick={() => setSelected(m)}
                  title={`View ${m.name}`}
                >
                  {m.imageUrl ? (
                    <SmartImg
                      src={imgSrc(m.imageUrl)}
                      alt={m.name}
                      className="w-full aspect-[4/5] object-cover rounded-xl border border-white/10 group-hover:opacity-90 transition"
                      width={360}
                      height={450}
                    />
                  ) : (
                    <div className="w-full aspect-[4/5] rounded-xl border border-white/10 bg-white/5 grid place-items-center text-xs text-slate-400">
                      No image
                    </div>
                  )}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-xl bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 sm:p-3 !text-white">
                    <div className="font-semibold !text-white text-sm sm:text-base">
                      {m.name}
                      {m.title && <span className="text-slate-300"> — {m.title}</span>}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )
      )}

      {/* Modal */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name || "Team"}
      >
        {selected && (
          <div className="space-y-3 !text-slate-200">
            {selected.imageUrl && (
              <SmartImg
                src={imgSrc(selected.imageUrl)}
                alt={selected.name}
                className="w-full aspect-[4/5] object-cover rounded-xl border border-white/10"
                width={480}
                height={600}
              />
            )}
            {selected.title && (
              <div className="text-sm !text-slate-300">{selected.title}</div>
            )}
            {selected.blurb && (
              <>
                <div className="pt-1 text-xs sm:text-sm uppercase tracking-wider text-slate-400">
                  Bio
                </div>
                <p className="whitespace-pre-line text-sm sm:text-base !text-slate-200">
                  {selected.blurb}
                </p>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
